import {
  type AdvertiseBlockValue,
  AdvertiseBlockValueResolver,
  type PageBlockLinkValue,
  type Site,
} from "@ihr-radioedit/inferno-core";
import type { Store } from "@inferno/renderer-shared-core";
import { MagicLink, resolveLocalBrand } from "@inferno/renderer-shared-ui";
import { isRight } from "fp-ts/lib/Either";
import { inject } from "mobx-react";
import type { PageBlockInterface } from "../Block.component";
import styles from "./Advertise.module.scss";

interface AdvertiseProps extends PageBlockInterface {
  store?: Store;
}

const withoutLeadingSlash = (id?: string | null) => id?.replace(/^(\/|%2F)+/i, "");

const resolveAdvertiseHref = (link: PageBlockLinkValue | undefined, site: Site) => {
  if (!link?.ref) {
    return undefined;
  }
  const localBrands = Object.fromEntries(site.micrositeReferences.map(({ _id, slug }) => [_id, slug]));
  if (link.type !== "brand") {
    return resolveLocalBrand({ ...link }, localBrands, site).href || undefined;
  }
  const brandId = Object.keys(localBrands).find(id => withoutLeadingSlash(id) === withoutLeadingSlash(link.ref));
  return brandId ? resolveLocalBrand({ ...link, ref: brandId }, localBrands, site).href : undefined;
};

export const Advertise = inject("store")(({ block, store }: AdvertiseProps) => {
  if (!store) {
    return null;
  }

  const decoded = AdvertiseBlockValueResolver.decode(block.value);
  const value: AdvertiseBlockValue = isRight(decoded) ? decoded.right : {};
  const { title, subtitle, button_text, link } = value;
  const destination = link?.[0];
  const href = resolveAdvertiseHref(destination, store.site);

  if (!title || !button_text || !href) {
    return null;
  }

  return (
    <div className={`component-advertise ${styles.advertise}`}>
      <div className={styles.content}>
        <div>
          <h2 className={styles.title}>{title}</h2>
          {subtitle ? <p className={styles.subtitle}>{subtitle}</p> : null}
        </div>
        <MagicLink to={href} target={destination?.target} context="start_advertising" className={styles.button}>
          {button_text}
        </MagicLink>
      </div>
    </div>
  );
});

export default Advertise;
